import React from 'react';
import { Link } from 'react-router-dom';
import ServiceHero from '../../components/services/ServiceHero';
import { Footer } from '../../components/Footer';
import { Check, Radio, Zap, TrendingUp, Users, Monitor, Wifi } from 'lucide-react';

const LiveStreamingPage = () => {

  const webinarUses = [
    'Course or program launch live → Immediate sales during the live',
    'Free masterclass to generate leads → Email list + qualified prospects',
    'Live Q&A with your community → Engagement and loyalty',
    'B2B product/service presentation → Live closing with prospects',
    'Paid live workshop → Immediate revenue + replay resold',
    'Expert interview in front of your audience → Authority and personal brand',
    'Multi-speaker conference → Premium high-impact event'
  ];

  const comparison = [
    { home: 'Blurry or poorly lit image', studio: 'Professional 4K image, perfect lighting' },
    { home: 'Poor sound (echo, background noise)', studio: 'Studio sound — professional microphone' },
    { home: 'Unstable internet', studio: 'Dedicated fiber + 4G backup' },
    { home: 'Technical stress during the live', studio: 'Dedicated technician handles everything' },
    { home: 'No edited replay', studio: 'HD replay delivered within 48h to resell' },
    { home: 'Amateur impression', studio: 'Maximum credibility and authority' }
  ];

  const packs = [
    {
      tier: '🥉',
      name: 'Streaming Small',
      price: 7000000,
      bestFor: 'short masterclass, live Q&A, live interview, first webinar',
      schedule: [
        { time: 'H1', title: 'Setup & Test', desc: 'Streaming setup, connection test, graphics test, client rehearsal' },
        { time: 'H2–H3', title: 'LIVE', desc: '2h live stream — 1 platform (YouTube or Facebook)' },
        { time: 'H3–H3:30', title: 'Backup & Close', desc: 'Recording save, final file check' }
      ],
      included: ['1 streaming platform', 'Dedicated technician', 'Internet backup', 'Webinar recording delivered'],
      idealFor: ['90-min free masterclass to launch an email list', 'Live Q&A with your community', 'Expert interview in front of your audience', 'Short offer or program presentation']
    },
    {
      tier: '🥈',
      name: 'Streaming Medium',
      price: 13000000,
      bestFor: 'launch webinar, paid workshop, live training, conference',
      popular: true,
      schedule: [
        { time: 'H1–H2', title: 'Setup & Rehearsal', desc: 'Full setup, multi-platform test, presenter rehearsal, slides & graphics test' },
        { time: 'H2–H6', title: 'LIVE', desc: '4h live stream — 2 simultaneous platforms' },
        { time: 'H6–H7', title: 'Backup & Close', desc: 'Save, organize files, delivery' }
      ],
      included: ['2 simultaneous platforms', 'Real-time slides integration', 'Professional on-screen graphics', 'Dedicated technician', 'Internet backup', 'HD recording delivered'],
      idealFor: ['Course launch webinar', 'Paid live workshop (4h intensive)', 'Corporate conference with multiple speakers', 'Premium masterclass with Q&A session']
    },
    {
      tier: '🥇',
      name: 'Streaming Full Day',
      price: 22000000,
      bestFor: 'major launch, online summit, full day live training, premium conference',
      schedule: [
        { time: 'H1–H3', title: 'Setup & Full Rehearsal', desc: 'Premium setup, full multi-platform test, general rehearsal with all speakers' },
        { time: 'H3–H9', title: 'LIVE', desc: '6h live stream — unlimited platforms' },
        { time: 'H9–H10', title: 'Backup & Pack Down', desc: 'Full save, file organization, equipment pack down' },
        { time: '48h', title: 'Replay Edit', desc: 'Full recording edited and delivered in HD' }
      ],
      included: ['Unlimited platforms', 'Complete graphics control', 'Real-time slides & visuals', '2 dedicated technicians', 'Fiber + 4G backup', 'HD recording delivered', 'Full replay edited within 48h'],
      idealFor: ['Premium program launch day', 'Multi-speaker online summit', 'Full day live bootcamp', 'Corporate multi-city general assembly']
    }
  ];

  const roiTable = [
    { pack: 'Streaming Small', price: 'Rp 7,000,000', revenue: 'Rp 20,000,000', roi: 'x 2.8' },
    { pack: 'Streaming Medium', price: 'Rp 13,000,000', revenue: 'Rp 20,000,000', roi: 'x 1.5' },
    { pack: 'Streaming Full Day', price: 'Rp 22,000,000', revenue: 'Rp 50,000,000 (25 sales)', roi: 'x 2.3' }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24">
      <ServiceHero
        tag="LIVE STREAMING"
        title="Webinars & Events"
        description="Multi-platform live streaming from our professional studio — YouTube, Facebook, LinkedIn, Instagram."
        bookLink="/services/live-streaming/book"
        bookLabel="Book Now"
        heroImage="https://images.unsplash.com/photo-1678982293419-aff97aa4aeea?w=1600&q=80"
        metaTitle="Live Streaming | Canggu Podcast Studio"
        metaDescription="Professional live streaming studio in Canggu, Bali. Multi-platform webinars and events with dedicated technician."
      />

      {/* Intro Block */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl">
            <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
              The webinar: your best live selling tool
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              A well-produced webinar from our studio is the difference between an amateur presentation 
              filmed at home and a professional stage that inspires trust, credibility, and action.
            </p>
            
            <h3 className="text-xl font-semibold text-white mb-4">What you can do in a webinar from our studio:</h3>
            <ul className="space-y-3">
              {webinarUses.map((use, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-400">
                  <Check className="w-5 h-5 text-[#F5A623] mt-0.5 flex-shrink-0" />
                  {use}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8 text-center">
            Why From Our Studio vs From Home
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left p-4 bg-red-500/10 text-red-400 border border-[#262626]">From Home</th>
                  <th className="text-left p-4 bg-green-500/10 text-green-400 border border-[#262626]">From Our Studio</th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((row, i) => (
                  <tr key={i}>
                    <td className="p-4 border border-[#262626] text-gray-400">{row.home}</td>
                    <td className="p-4 border border-[#262626] text-white">{row.studio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Streaming Packs */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8">Streaming Packs</h2>
          
          <div className="space-y-8">
            {packs.map((pack, index) => (
              <div 
                key={index}
                className={`p-6 md:p-8 border ${
                  pack.popular 
                    ? 'border-[#F5A623] bg-[#F5A623]/5' 
                    : 'border-[#262626] bg-[#121212]'
                } relative`}
              >
                {pack.popular && (
                  <span className="absolute -top-3 left-8 px-4 py-1 bg-[#F5A623] text-black text-sm font-semibold">
                    MOST POPULAR
                  </span>
                )}
                
                <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-3xl">{pack.tier}</span>
                      <h3 className="text-2xl font-heading font-semibold text-white">{pack.name}</h3>
                    </div>
                    <p className="text-gray-400 text-sm">Best for: {pack.bestFor}</p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <div className="text-3xl font-heading font-bold text-[#F5A623]">
                      Rp {(pack.price / 1000000).toFixed(0)}M
                    </div>
                  </div>
                </div>

                {/* Schedule */}
                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3">Schedule</h4>
                  <div className="space-y-2">
                    {pack.schedule.map((item, i) => (
                      <div key={i} className="flex gap-4 p-3 bg-[#0D0D0D] border border-[#262626]">
                        <span className="text-[#F5A623] font-semibold w-20 flex-shrink-0">{item.time}</span>
                        <div>
                          <span className="text-white font-medium">{item.title}</span>
                          <span className="text-gray-400 ml-2">— {item.desc}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Included */}
                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3">Included</h4>
                  <div className="flex flex-wrap gap-2">
                    {pack.included.map((item, i) => (
                      <span key={i} className="px-3 py-1 bg-[#F5A623]/10 text-[#F5A623] text-sm border border-[#F5A623]/30">
                        {item}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Ideal For */}
                <div>
                  <h4 className="text-white font-semibold mb-3">Ideal For</h4>
                  <ul className="grid md:grid-cols-2 gap-2">
                    {pack.idealFor.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-gray-400 text-sm">
                        <span className="text-[#F5A623]">→</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Table */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-8 text-center">
            <TrendingUp className="inline w-8 h-8 text-[#F5A623] mr-3" />
            Return on Investment
          </h2>
          <p className="text-center text-gray-400 mb-8">If 10 sales at Rp 2,000,000 each</p>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#121212]">
                  <th className="text-left p-4 text-white border border-[#262626]">Pack</th>
                  <th className="text-left p-4 text-white border border-[#262626]">Price</th>
                  <th className="text-left p-4 text-white border border-[#262626]">Revenue</th>
                  <th className="text-left p-4 text-[#F5A623] border border-[#262626]">ROI</th>
                </tr>
              </thead>
              <tbody>
                {roiTable.map((row, i) => (
                  <tr key={i}>
                    <td className="p-4 border border-[#262626] text-gray-300">{row.pack}</td>
                    <td className="p-4 border border-[#262626] text-gray-400">{row.price}</td>
                    <td className="p-4 border border-[#262626] text-green-400">{row.revenue}</td>
                    <td className="p-4 border border-[#262626] text-[#F5A623] font-bold">{row.roi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Callout */}
          <div className="mt-8 p-6 bg-[#F5A623]/10 border border-[#F5A623]/30 text-center">
            <p className="text-white text-lg">
              "One well-produced webinar can cover the studio cost and generate sales for months through the resold replay."
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-4">
            Ready to Go Live?
          </h2>
          <p className="text-gray-400 mb-8">
            Book your streaming session and reach your audience professionally.
          </p>
          <Link
            to="/services/live-streaming/book"
            className="inline-block px-12 py-4 bg-[#F5A623] text-black font-semibold text-lg hover:bg-[#FFB84D] transition-colors"
            data-testid="book-now-cta"
          >
            Book Now
          </Link>
        </div>
      </section>

      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LiveStreamingPage;
